import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ApiError } from "../../lib/api";
import { resetPassword } from "../../lib/auth";

export function ResetPasswordPage(): JSX.Element {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [token, setToken] = useState(params.get("token") ?? "");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await resetPassword({ email, token, password });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.problem.detail || err.problem.title);
      } else {
        setError("Couldn't reset password. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col justify-center gap-6 px-6 py-12">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-neutral-900">Choose a new password</h1>
        <p className="text-sm text-neutral-600">
          For your security, all existing sessions will be signed out.
        </p>
      </header>

      {done ? (
        <div className="space-y-4 rounded-lg border border-success-200 bg-success-50 p-6 text-success-700">
          <p>Password updated. Sign in with your new password.</p>
          <Link to="/login">
            <Button variant="primary">Continue to sign-in</Button>
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label="Reset token"
            required
            minLength={64}
            maxLength={64}
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
          <Input
            label="New password"
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            hint="Min 12 characters."
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error !== null && (
            <p
              role="alert"
              className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
            >
              {error}
            </p>
          )}
          <Button type="submit" variant="primary" fullWidth loading={submitting}>
            Update password
          </Button>
        </form>
      )}
    </main>
  );
}
